import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Evie & Bear "Complete Looks" page (sections/eb-complete-looks.liquid): one look with every piece, a size picker
 * and a checkbox per piece, and "Add the complete look" (one request, opens the cart drawer). All checked pieces carry
 * _look and _look_item: 'yes', so the look discount app takes its 20% as soon as two or more pieces are in the cart.
 * Adds go through the same Ajax route and CartLinesUpdateEvent as Horizon's product form.
 * Compact cards (phones, or the "compact" setting): a tap on the card moves the pieces (data-cls-body) into the
 * section's bottom sheet (<dialog data-cls-sheet>) and back when it closes; listeners sit on the body, so they move along.
 */
class EbLookSet extends HTMLElement {
  #busy = false;
  /** @type {HTMLElement | null} */
  #body = null;

  connectedCallback() {
    if (this.#body) return; // moved in the page (continued sections join the first grid): listeners are already set
    const body = this.querySelector('[data-cls-body]');
    if (!(body instanceof HTMLElement)) return;
    this.#body = body;
    body.addEventListener('change', (event) => this.#onChange(event));
    body.addEventListener('click', (event) => this.#onSwapClick(event));
    body.querySelector('[data-cls-add]')?.addEventListener('click', () => this.#add());
    this.querySelector('[data-cls-open]')?.addEventListener('click', () => this.#open());
    this.#update();
  }

  get #compact() {
    return !!this.closest('.eb-cls--compact') || window.matchMedia('(max-width: 749px)').matches;
  }

  /** Open this look's pieces in the bottom sheet (compact cards only). */
  #open() {
    const sheet = this.closest('.eb-cls')?.querySelector('[data-cls-sheet]');
    const slot = sheet?.querySelector('[data-cls-sheet-body]');
    if (!this.#compact || !(sheet instanceof HTMLDialogElement) || !slot || !this.#body) return;
    slot.append(this.#body);
    sheet.addEventListener('close', () => this.append(/** @type {HTMLElement} */ (this.#body)), { once: true });
    sheet.showModal();
    sheet.scrollTop = 0;
  }

  /** @param {string} selector */
  #q(selector) {
    return this.#body?.querySelector(selector) ?? null;
  }

  get #pieces() {
    return [...(this.#body?.querySelectorAll('[data-cls-piece]') ?? [])].filter((el) => el instanceof HTMLElement);
  }

  /**
   * Swap: "Swap" opens a row of alternatives under the piece (same kind of item, from data-cls-alts); picking one
   * replaces the piece (photo, name, price, size picker) and puts the old piece back in the list.
   * @param {MouseEvent} event
   */
  #onSwapClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    const toggle = target?.closest('[data-cls-swap]');
    const pick = target?.closest('[data-cls-pick]');
    const piece = (toggle || pick)?.closest('[data-cls-piece]');
    if (!(piece instanceof HTMLElement)) return;
    if (toggle instanceof HTMLButtonElement) {
      const open = toggle.getAttribute('aria-expanded') === 'true';
      this.#body?.querySelectorAll('.eb-cls__alts').forEach((el) => el.remove());
      this.#body?.querySelectorAll('[data-cls-swap]').forEach((el) => el.setAttribute('aria-expanded', 'false'));
      if (!open) {
        piece.append(this.#altList(piece));
        toggle.setAttribute('aria-expanded', 'true');
      }
      return;
    }
    if (pick instanceof HTMLElement) {
      const alts = this.#json(piece, '[data-cls-alts]') || [];
      const current = this.#json(piece, '[data-cls-current]');
      const chosen = alts.find((/** @type {any} */ a) => String(a.id) === pick.dataset.clsPick);
      if (!chosen || !current) return;
      this.#fill(piece, chosen);
      this.#setJson(piece, '[data-cls-current]', chosen);
      this.#setJson(piece, '[data-cls-alts]', [current, ...alts.filter((/** @type {any} */ a) => a !== chosen)]);
      piece.querySelector('.eb-cls__alts')?.remove();
      piece.querySelector('[data-cls-swap]')?.setAttribute('aria-expanded', 'false');
      this.#update();
      this.#announce(`${chosen.title} ✓`);
    }
  }

  /** @param {HTMLElement} piece @param {string} sel */
  #json(piece, sel) {
    try {
      return JSON.parse(piece.querySelector(sel)?.textContent || 'null');
    } catch {
      return null;
    }
  }

  /** @param {HTMLElement} piece @param {string} sel @param {any} value */
  #setJson(piece, sel, value) {
    const el = piece.querySelector(sel);
    if (el) el.textContent = JSON.stringify(value);
  }

  /** @param {HTMLElement} piece */
  #altList(piece) {
    const list = document.createElement('div');
    list.className = 'eb-cls__alts';
    const heading = this.closest('.eb-cls')?.getAttribute('data-swap-heading') || '';
    list.innerHTML = `<p class="eb-cls__alts-title">${heading}</p><ul class="eb-cls__alts-row"></ul>`;
    const row = list.querySelector('ul');
    for (const a of this.#json(piece, '[data-cls-alts]') || []) {
      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'eb-cls__alt';
      btn.dataset.clsPick = String(a.id);
      btn.innerHTML = `<img src="${a.img}" alt="" width="64" height="80"><span class="eb-cls__alt-name"></span><span class="eb-cls__alt-price"></span>`;
      /** @type {HTMLElement} */ (btn.querySelector('.eb-cls__alt-name')).textContent = a.title;
      /** @type {HTMLElement} */ (btn.querySelector('.eb-cls__alt-price')).textContent = a.money;
      li.append(btn);
      row?.append(li);
    }
    return list;
  }

  /** Put another product into a piece row. @param {HTMLElement} piece @param {any} p */
  #fill(piece, p) {
    piece.dataset.price = String(p.price);
    piece.querySelectorAll('a.eb-cls__thumb, a.eb-cls__name').forEach((a) => a instanceof HTMLAnchorElement && (a.href = p.url));
    const img = piece.querySelector('.eb-cls__thumb img');
    if (img instanceof HTMLImageElement) {
      img.removeAttribute('srcset');
      img.src = p.img;
    }
    const name = piece.querySelector('.eb-cls__name');
    if (name) name.textContent = p.title;
    const price = piece.querySelector('.eb-cls__price');
    if (price) price.textContent = p.money;
    const check = piece.querySelector('[data-cls-include]');
    if (check) check.setAttribute('aria-label', p.title);
    const old = piece.querySelector('[data-cls-variant]');
    const oldLabel = old instanceof HTMLSelectElement ? piece.querySelector(`label[for="${old.id}"]`) : piece.querySelector('.eb-cls__one');
    const id = old?.id || `Cls-${p.id}-${Math.random().toString(36).slice(2, 7)}`;
    let field;
    if (p.single) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.value = String((p.variants.find((/** @type {any} */ v) => v.available) || p.variants[0]).id);
      const one = document.createElement('span');
      one.className = 'eb-cls__one';
      one.textContent = 'One size';
      oldLabel?.replaceWith(one);
    } else {
      field = document.createElement('select');
      field.id = id;
      field.className = 'eb-cls__select';
      const placeholder = old instanceof HTMLSelectElement ? old.options[0]?.textContent : 'Choose size';
      field.add(new Option(placeholder || 'Choose size', '', true, true));
      for (const v of p.variants) {
        const o = new Option(v.available ? v.title : `${v.title} — sold out`, String(v.id));
        o.dataset.price = String(v.price);
        o.disabled = !v.available;
        field.add(o);
      }
      const label = document.createElement('label');
      label.className = 'visually-hidden';
      label.htmlFor = id;
      label.textContent = p.title;
      if (oldLabel) oldLabel.replaceWith(label);
      else old?.before(label);
    }
    field.setAttribute('data-cls-variant', '');
    old?.replaceWith(field);
  }

  /** @param {Event} event */
  #onChange(event) {
    const target = event.target;
    if (target instanceof HTMLSelectElement) target.classList.remove('is-needed');
    if (target instanceof HTMLInputElement && target.matches('[data-cls-include]')) {
      target.closest('[data-cls-piece]')?.classList.toggle('is-off', !target.checked);
    }
    this.#update();
  }

  /** Price of a piece in minor units: the chosen variant, else the first available one. @param {HTMLElement} piece */
  #price(piece) {
    const field = piece.querySelector('[data-cls-variant]');
    const option = field instanceof HTMLSelectElement ? field.selectedOptions[0] : null;
    return Number(option?.dataset.price || piece.dataset.price || 0);
  }

  get #checked() {
    return this.#pieces.filter((piece) => piece.querySelector('[data-cls-include]')?.matches(':checked'));
  }

  #update() {
    const checked = this.#checked;
    const percent = checked.length >= 2 ? Number(this.dataset.discount) || 0 : 0;
    // Same as the cart: the discount is taken per item and rounded down to the cent
    const total = checked.reduce((sum, piece) => sum + this.#price(piece), 0);
    const discounted = checked.reduce((sum, piece) => {
      const cents = this.#price(piece);
      return sum + cents - Math.floor((cents * percent) / 100);
    }, 0);
    const was = this.#q('[data-cls-was]');
    const now = this.#q('[data-cls-total]');
    if (now) now.textContent = this.#money(discounted);
    // Store credit on the look: same rounding as the Flow workflow (price paid × percent / 100, to whole cents)
    const credit = this.#q('[data-cls-credit]');
    const creditAmount = this.#q('[data-cls-credit-amount]');
    if (credit instanceof HTMLElement) credit.hidden = discounted === 0;
    if (creditAmount) creditAmount.textContent = this.#money(Math.round((discounted * (Number(this.dataset.creditPercent) || 0)) / 100));
    if (was instanceof HTMLElement) {
      was.hidden = discounted === total;
      was.textContent = this.#money(total);
    }
    const add = this.#q('[data-cls-add]');
    if (add instanceof HTMLButtonElement) add.disabled = this.#busy || checked.length === 0;
  }

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '${{amount}}', this.dataset.currency || 'USD');
  }

  /** @param {string} message */
  #announce(message) {
    const live = this.#q('[data-cls-live]');
    if (!live) return;
    live.textContent = '';
    requestAnimationFrame(() => (live.textContent = message));
  }

  async #add() {
    if (this.#busy) return;
    const checked = this.#checked;
    const missing = checked
      .map((piece) => piece.querySelector('[data-cls-variant]'))
      .filter((field) => field instanceof HTMLSelectElement && !field.value);
    if (missing.length) {
      missing.forEach((field) => field?.classList.add('is-needed'));
      if (missing[0] instanceof HTMLElement) missing[0].focus();
      this.#announce(this.dataset.textChoose || '');
      return;
    }

    const properties = checked.length >= 2 ? { _look: this.dataset.look, _look_item: 'yes' } : {};
    const items = checked.map((piece) => {
      const field = piece.querySelector('[data-cls-variant]');
      return { id: Number(field instanceof HTMLInputElement || field instanceof HTMLSelectElement ? field.value : 0), quantity: 1, properties };
    });

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);

    this.#busy = true;
    this.#update();
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const data = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      }).then((r) => r.json());
      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      if (data.status) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'eb-look-set', itemCount: 0 },
        });
        this.dispatchEvent(new CartErrorEvent({ error: data.message || 'Add to cart failed', code: 'INVALID' }));
        this.#announce(data.description || data.message || this.dataset.textError || '');
        return;
      }
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: false, items: cart.items, sections: data.sections, source: 'eb-look-set', itemCount: items.length },
      });
      this.#announce(this.dataset.textAdded || '');
      const sheet = this.#body?.closest('dialog');
      if (sheet instanceof HTMLDialogElement && sheet.open) sheet.close();
      const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
      if (drawer && 'open' in drawer && typeof drawer.open === 'function' && !(/** @type {any} */ (drawer).isOpen)) drawer.open();
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: String(error), code: 'SERVICE_UNAVAILABLE' }));
      this.#announce(this.dataset.textError || '');
    } finally {
      this.#busy = false;
      this.#update();
    }
  }
}

if (!customElements.get('eb-look-set')) customElements.define('eb-look-set', EbLookSet);

/** Bottom sheet: close button and a tap on the backdrop close it. */
for (const sheet of document.querySelectorAll('[data-cls-sheet]')) {
  if (!(sheet instanceof HTMLDialogElement)) continue;
  sheet.querySelector('[data-cls-close]')?.addEventListener('click', () => sheet.close());
  sheet.addEventListener('click', (event) => {
    if (event.target !== sheet) return;
    const r = sheet.getBoundingClientRect();
    const inside = event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom;
    if (!inside) sheet.close();
  });
}

/** Filter chips: show only looks whose data-cls-tags contain the chosen tag. Sections marked data-cls-continued
 *  (more than 50 looks on one page) that follow the filter row join its grid and are counted in the chips. */
for (const group of document.querySelectorAll('[data-cls-filters]')) {
  const root = group.closest('.eb-cls');
  if (!root) continue;
  /** @type {Element[]} */
  const roots = [root];
  let next = root.closest('.shopify-section')?.nextElementSibling;
  while (next?.querySelector('.eb-cls[data-cls-continued]')) {
    roots.push(/** @type {Element} */ (next.querySelector('.eb-cls[data-cls-continued]')));
    next = next.nextElementSibling;
  }
  const looks = () => roots.flatMap((r) => [...r.querySelectorAll('eb-look-set')]).filter((el) => el instanceof HTMLElement);
  if (roots.length > 1) {
    // One grid: the looks of the continued sections join the first one, so the sticky filter row stays on screen
    const grid = root.querySelector('.eb-cls__grid');
    roots.slice(1).forEach((r) => {
      grid?.append(...r.querySelectorAll('eb-look-set'));
      const wrap = r.closest('.shopify-section');
      if (wrap instanceof HTMLElement) wrap.hidden = true;
    });
    const count = (/** @type {string} */ tag) =>
      looks().filter((el) => tag === 'all' || (el.dataset.clsTags || '').split(' ').includes(tag)).length;
    group.querySelectorAll('[data-cls-filter]').forEach((chip) => {
      const span = chip.querySelector('span');
      if (chip instanceof HTMLElement && span) span.textContent = String(count(chip.dataset.clsFilter || 'all'));
    });
  }
  group.addEventListener('click', (event) => {
    const chip = event.target instanceof Element ? event.target.closest('[data-cls-filter]') : null;
    if (!(chip instanceof HTMLElement)) return;
    const tag = chip.dataset.clsFilter || 'all';
    group.querySelectorAll('[data-cls-filter]').forEach((el) => el.setAttribute('aria-pressed', String(el === chip)));
    let shown = 0;
    looks().forEach((look) => {
      const tags = (look.dataset.clsTags || '').split(' ');
      const show = tag === 'all' || tags.includes(tag);
      look.hidden = !show;
      if (show) shown += 1;
    });
    const none = root.querySelector('[data-cls-none]');
    if (none instanceof HTMLElement) none.hidden = shown > 0;
    // Back to the first look when the list starts above the screen (the filter row sticks under the header)
    const grid = root.querySelector('.eb-cls__grid');
    if (grid && grid.getBoundingClientRect().top < group.getBoundingClientRect().bottom) {
      window.scrollTo({ top: window.scrollY + grid.getBoundingClientRect().top - group.getBoundingClientRect().bottom - 8 });
    }
  });
}
